import { useState } from 'react'
import {
  useCloseFieldMessage,
  useCustomers,
  useFieldMessages,
  useProjects,
  useSendFieldMessage,
  useUsers,
  useUsersOnJob
} from '../api/queries'
import type { FieldMessage, FieldMessageSend } from '../api/types'
import { projectOptionLabel } from '../lib/projectLabel'
import { Avatar, Card, EmptyState, ErrorBox, Loading } from '../components/ui'

function formatTime(value?: string | null) {
  if (!value) return '-'
  return new Date(value).toLocaleString('da-DK', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  })
}

/** "3 min", "1 t 20 min" - hvor længe der gik, før han så den. */
function formatMinutes(minutes: number) {
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest === 0 ? `${hours} t` : `${hours} t ${rest} min`
}

/**
 * Kontorets overblik over akutte udkald og beskeder (beslutning 060).
 *
 * Det, siden findes for, er kvitteringen: har han set det, og hvor lang tid gik
 * der. Uden den ville man ringe alligevel - og så var modulet spildt.
 */
export function FieldMessagesPage() {
  const [includeClosed, setIncludeClosed] = useState(false)
  const [sending, setSending] = useState<'urgent' | 'job_note' | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const { data, isLoading, error } = useFieldMessages({ includeClosed, days: 14 })
  const close = useCloseFieldMessage()

  const messages = data ?? []
  const waiting = messages.filter((m) => m.status === 'sent')

  async function cancel(message: FieldMessage) {
    setActionError(null)
    if (!confirm(`Afblæs "${message.title}"?`)) return

    try {
      await close.mutateAsync({ id: message.id, cancelled: true, outcome: 'Afblæst af kontoret' })
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Kunne ikke afblæses.')
    }
  }

  return (
    <>
      <div className="cal-toolbar">
        <div className="cal-title">
          {waiting.length > 0 ? `${waiting.length} venter på kvittering` : 'Alt er set'}
        </div>

        <label className="cal-person" style={{ cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={includeClosed}
            onChange={(e) => setIncludeClosed(e.target.checked)}
          />
          Vis afsluttede
        </label>

        <button className="btn btn--danger btn--sm" onClick={() => setSending('urgent')}>
          🚨 Akut udkald
        </button>
        <button className="btn btn--primary btn--sm" onClick={() => setSending('job_note')}>
          🔔 Besked til et job
        </button>
      </div>

      {waiting.length > 0 && (
        <div className="banner" style={{ marginBottom: 12, borderLeft: '4px solid #DC2626' }}>
          <strong>
            {waiting.length} {waiting.length === 1 ? 'besked er' : 'beskeder er'} ikke set endnu.
          </strong>{' '}
          Haster det, så ring - systemet erstatter ikke et opkald.
        </div>
      )}

      {actionError && <ErrorBox error={actionError} />}
      {error && <ErrorBox error={error} />}
      {isLoading && <Loading />}

      {data && messages.length === 0 && (
        <EmptyState>
          Der er ikke sendt noget. Et <strong>akut udkald</strong> går til én mand og lyser rødt i
          hans app; en <strong>besked til et job</strong> lægger sig oven i det, han står med.
        </EmptyState>
      )}

      {messages.length > 0 && (
        <Card>
          <div className="stack" style={{ gap: 10 }}>
            {messages.map((m) => (
              <div key={m.id} className="row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <span
                  style={{
                    width: 6,
                    alignSelf: 'stretch',
                    minHeight: 44,
                    borderRadius: 3,
                    background: m.statusColor,
                    flexShrink: 0
                  }}
                />

                <div style={{ minWidth: 210, flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>
                    {m.isUrgent && <span style={{ color: '#DC2626' }}>🚨 </span>}
                    {m.title}
                  </div>
                  <div className="small muted">
                    {m.customerName ?? m.projectName ?? m.kindName}
                    {m.site?.address && ` · ${m.site.address}`}
                  </div>
                  {m.outcome && <div className="tiny">{m.outcome}</div>}
                </div>

                <div className="row" style={{ gap: 8, alignItems: 'center', minWidth: 160 }}>
                  <Avatar user={m.recipient} size={26} />
                  <div>
                    <div className="small">{m.recipient.displayName}</div>
                    <div className="tiny muted">{formatTime(m.createdAt)}</div>
                  </div>
                </div>

                {/* Kvitteringen er det, siden findes for. */}
                <div style={{ minWidth: 150 }}>
                  <div
                    className="small"
                    style={{ color: m.statusColor, fontWeight: m.status === 'sent' ? 600 : 400 }}
                  >
                    {m.statusName}
                  </div>
                  <div className="tiny muted">
                    {m.minutesToAcknowledge !== null && m.minutesToAcknowledge !== undefined
                      ? `Set efter ${formatMinutes(m.minutesToAcknowledge)}`
                      : `Sendt for ${formatMinutes(m.minutesOpen)} siden`}
                  </div>
                </div>

                {m.isOpen && (
                  <button className="btn btn--sm" onClick={() => void cancel(m)}>
                    Afblæs
                  </button>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {sending && <SendDialog kind={sending} onClose={() => setSending(null)} />}
    </>
  )
}

function SendDialog({ kind, onClose }: { kind: 'urgent' | 'job_note'; onClose: () => void }) {
  const isUrgent = kind === 'urgent'
  const { data: users } = useUsers()
  const { data: customers } = useCustomers()
  const { data: projects } = useProjects({ pageSize: 200 })
  const send = useSendFieldMessage()

  const [form, setForm] = useState<FieldMessageSend>({
    kind,
    title: '',
    description: '',
    recipientUserIds: [],
    projectId: null,
    customerId: null,
    address: '',
    postalCode: '',
    city: '',
    contactName: '',
    contactPhone: ''
  })
  const [error, setError] = useState<string | null>(null)

  // Hvem er paa sagen i dag? Hentes fra kalenderen, saa kontoret ikke skal huske det.
  const { data: onJob } = useUsersOnJob(!isUrgent ? (form.projectId ?? null) : null)

  function toggle(userId: number) {
    setForm((f) => ({
      ...f,
      recipientUserIds: isUrgent
        ? // Ét udkald, én mand - derfor erstattes valget frem for at laegges til.
          [userId]
        : f.recipientUserIds.includes(userId)
          ? f.recipientUserIds.filter((id) => id !== userId)
          : [...f.recipientUserIds, userId]
    }))
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      await send.mutateAsync({
        ...form,
        description: form.description?.trim() || null,
        address: form.address?.trim() || null,
        postalCode: form.postalCode?.trim() || null,
        city: form.city?.trim() || null,
        contactName: form.contactName?.trim() || null,
        contactPhone: form.contactPhone?.trim() || null
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Beskeden kunne ikke sendes.')
    }
  }

  return (
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="modal__card">
        <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>
          {isUrgent ? '🚨 Akut udkald' : '🔔 Besked til et job'}
        </h2>
        <p className="small muted" style={{ marginTop: 0 }}>
          {isUrgent
            ? 'Går til én mand og lyser rødt i hans app. Haster det, så ring også - systemet erstatter ikke et opkald.'
            : 'Lægger sig oven i det, han står med. Hver mand kvitterer for sig, så du kan se, hvem der har set den.'}
        </p>

        <form onSubmit={(e) => void submit(e)}>
          <label className="field">
            <span className="field__label">Hvad handler det om</span>
            <input
              value={form.title}
              required
              placeholder={
                isUrgent ? 'F.eks. Strømsvigt i teknikrum' : 'F.eks. Husk firmware på kamera 3'
              }
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </label>

          <label className="field">
            <span className="field__label">Uddybning (valgfri)</span>
            <textarea
              rows={2}
              value={form.description ?? ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </label>

          <label className="field">
            <span className="field__label">{isUrgent ? 'Sag (valgfri)' : 'Hvilket job'}</span>
            <select
              value={form.projectId ?? 0}
              required={!isUrgent}
              onChange={(e) =>
                setForm({ ...form, projectId: Number(e.target.value) || null, recipientUserIds: [] })
              }
            >
              <option value={0}>{isUrgent ? 'Ingen bestemt sag' : 'Vælg sag ...'}</option>
              {(projects?.items ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {projectOptionLabel(p)}
                </option>
              ))}
            </select>
          </label>

          {!isUrgent && onJob && onJob.length > 0 && (
            <p className="tiny muted" style={{ marginTop: 0 }}>
              {onJob.length === 1 ? 'Én mand er' : `${onJob.length} mand er`} på sagen i dag:{' '}
              {onJob.map((u) => u.displayName).join(', ')}
            </p>
          )}

          <div className="field">
            <span className="field__label">
              {isUrgent ? 'Hvem sendes ud (én mand)' : 'Hvem skal have den'}
            </span>
            <div className="cal-people" style={{ maxHeight: 160, overflowY: 'auto' }}>
              {(users ?? [])
                .filter((u) => u.isActive)
                // Dem der er paa sagen i dag staar oeverst.
                .sort((a, b) => {
                  const aOn = onJob?.some((u) => u.id === a.id) ? 0 : 1
                  const bOn = onJob?.some((u) => u.id === b.id) ? 0 : 1
                  return aOn - bOn || a.displayName.localeCompare(b.displayName, 'da')
                })
                .map((u) => (
                  <label key={u.id} className="cal-person" style={{ cursor: 'pointer' }}>
                    <input
                      type={isUrgent ? 'radio' : 'checkbox'}
                      name="recipient"
                      checked={form.recipientUserIds.includes(u.id)}
                      onChange={() => toggle(u.id)}
                    />
                    {u.displayName}
                    {onJob?.some((o) => o.id === u.id) && (
                      <span className="tiny" style={{ color: '#059669' }}>
                        {' '}
                        · på sagen i dag
                      </span>
                    )}
                  </label>
                ))}
            </div>
          </div>

          {isUrgent && (
            <>
              <label className="field">
                <span className="field__label">Kunde (valgfri)</span>
                <select
                  value={form.customerId ?? 0}
                  onChange={(e) => setForm({ ...form, customerId: Number(e.target.value) || null })}
                >
                  <option value={0}>Ingen bestemt kunde</option>
                  {(customers ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="field">
                <span className="field__label">Hvor skal han hen</span>
                <input
                  value={form.address ?? ''}
                  placeholder="Vej og nummer"
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                />
              </label>

              <div className="row" style={{ gap: 10 }}>
                <label className="field" style={{ width: 110 }}>
                  <span className="field__label">Postnr.</span>
                  <input
                    value={form.postalCode ?? ''}
                    onChange={(e) => setForm({ ...form, postalCode: e.target.value })}
                  />
                </label>
                <label className="field" style={{ flex: 1 }}>
                  <span className="field__label">By</span>
                  <input
                    value={form.city ?? ''}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                  />
                </label>
              </div>

              <div className="row" style={{ gap: 10 }}>
                <label className="field" style={{ flex: 1 }}>
                  <span className="field__label">Hvem ringede</span>
                  <input
                    value={form.contactName ?? ''}
                    onChange={(e) => setForm({ ...form, contactName: e.target.value })}
                  />
                </label>
                <label className="field" style={{ flex: 1 }}>
                  <span className="field__label">Telefon</span>
                  <input
                    value={form.contactPhone ?? ''}
                    placeholder="Så han kan ringe på vejen"
                    onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
                  />
                </label>
              </div>
            </>
          )}

          {error && <ErrorBox error={error} />}

          <div className="row" style={{ gap: 8, marginTop: 12 }}>
            <button
              className={isUrgent ? 'btn btn--danger' : 'btn btn--primary'}
              type="submit"
              disabled={send.isPending || form.recipientUserIds.length === 0}
            >
              {send.isPending ? 'Sender ...' : isUrgent ? 'Send udkald' : 'Send besked'}
            </button>
            <button className="btn" type="button" onClick={onClose}>
              Annullér
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
